import { useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import PageHeader from "../../../component/pageHeaders";
import {
  Button,
  DatePicker,
  Form,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Spin,
  Tag,
  message,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import ThumbnailPicker from "./thumbnailPicker";
import PagesList from "./pagesList";
import axios from "../../../config/axiosInstance";
import dayjs from "dayjs";
import { API } from "../../../config/API";
import { useParams, useNavigate } from "react-router-dom";
import Loadingbox from "../../../component/loadingbox";
import editons from "./editions.json";
import { convertApi } from "../helpers/compressUpload";
import moment from "moment";
import { useSelector } from "react-redux";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

function EpaperForm(props: any) {
  let { id }: any = useParams();
  const navigation = useNavigate();
  const User = useSelector((state: any) => state.User.user);
  const [isloading, setIsloading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState(null) as any;
  const [data, setData] = useState([]) as any;
  const [currentUploadIndex, setCurrentUploadIndex] = useState<any>(null);
  const [form] = Form.useForm();

  const [edition, setEdition] = useState(null);
  const [date, setDate] = useState();
  const [thumbnail, setThumbnail] = useState(null);
  const [pages, setPages] = useState<any[]>([]);
  const [scheduled, setScheduled] = useState<any>();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [tempScheduled, setTempScheduled] = useState<any>(null);
  const [uploadProgress, setUploadProgress] = useState({
    uploading: false,
    current: 0,
    total: 0,
  });
  const uploadCancelledRef = useRef(false);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      if (id !== "create") {
        let url = API.BASE_URL + API.EPAPER_DETAILS + id;
        const results = await axios.get(url);
        if (results?.data?.data) {
          let data = results?.data?.data?.attributes;
          setData(data);
          setEdition(data?.edition);
          setDate(data?.date);
          setThumbnail(data?.image);
          setPages(data?.epapercollections);
          setScheduled(data?.scheduledAt ? dayjs(data.scheduledAt) : null);
        }
        setIsloading(false);
      } else {
        setIsloading(false);
      }
    } catch (err) {
      setIsloading(false);
    }
  };

  const Finished = async (type: any, scheduledOverride?: any) => {
    try {
      const scheduledValue =
        scheduledOverride !== undefined ? scheduledOverride : scheduled;
      let values = form.getFieldsValue();
      if (type === "scheduleat") {
        if (!values?.edition) {
          message.error("Please select edition");
          return;
        }
        if (!scheduledValue) {
          message.error("Scheduled date & time is required");
          return;
        }

        // Validate scheduled time is not in the past
        if (scheduledValue && scheduledValue.isBefore(dayjs())) {
          message.error("Cannot schedule for past date/time");
          return;
        }
      }

      if (pages?.length) {
        setLoading(true);
        setType(type);
        let currentDate = new Date();
        currentDate.setHours(currentDate.getHours() + 6);
        var uploadPage = await uploadPages(pages, currentDate);

        if (uploadPage === null) {
          // Upload was cancelled
          setLoading(false);
          setCurrentUploadIndex(null);
          message.info("Upload cancelled");
          return;
        }

        // Use the user-selected Publish Date from the form, set to noon IST
        // (6:30 AM UTC) so the date never rolls back to the previous day in UTC.
        const selectedDate = values?.date
          ? dayjs(values.date).tz("Asia/Kolkata").startOf("day").add(12, "hours").toDate()
          : data?.date
            ? dayjs(data.date).tz("Asia/Kolkata").startOf("day").add(12, "hours").toDate()
            : dayjs().tz("Asia/Kolkata").startOf("day").add(12, "hours").toDate();

        var requestObj = {
          data: {
            edition: values?.edition,
            image: thumbnail,
            epapercollections: uploadPage,
            date: selectedDate,
            user: id !== "create" ? data?.user?.id : User?.id,
            publishedAt:
              type === "publish" ? new Date() : data?.publishedAt || null,
            scheduledAt:
              type === "scheduleat"
                ? scheduledValue?.toISOString()
                : type === "publish"
                  ? null
                  : data?.scheduledAt || null,
          },
        };
        let url = id !== "create" ? API.EPAPER_EDIT + id : API.EPAPER_ADD;
        let method = id !== "create" ? "PUT" : "POST";

        const submit = (obj: any) =>
          axios({
            method: method,
            url: API.BASE_URL + url,
            data: obj,
          })
            .then((response) => {
              setLoading(false);
              if (response?.data?.data?.id) {
                const resData =
                  response?.data?.data?.attributes || response?.data?.data;
                message.success(
                  `Epaper ${id !== "create" ? "Updated" : "Created"} Successfully.`,
                );
                // Update local state instead of full page reload
                setData({
                  ...data,
                  ...resData,
                  edition: resData?.edition || values?.edition,
                  image: resData?.image || thumbnail,
                  date: resData?.date || requestObj.data.date,
                  publishedAt:
                    resData?.publishedAt || requestObj.data.publishedAt,
                  scheduledAt:
                    resData?.scheduledAt || requestObj.data.scheduledAt,
                  epapercollections: resData?.epapercollections || uploadPage,
                  user: resData?.user || { id: requestObj.data.user },
                });
                setPages(resData?.epapercollections || uploadPage);
                if (resData?.scheduledAt) {
                  setScheduled(dayjs(resData.scheduledAt));
                } else if (type === "publish") {
                  setScheduled(null);
                }
                // If this was a create, navigate to the edit URL without reload
                if (id === "create") {
                  navigation(`/Auth/EpaperForm/${response.data.data.id}`, {
                    replace: true,
                  });
                }
              }
            })
            .catch((error) => {
              setLoading(false);
              const body = error?.response?.data;
              if (
                id === "create" &&
                error?.response?.status === 409 &&
                body?.code === "DUPLICATE_EPAPER"
              ) {
                const existing = body.existing || {};
                Modal.confirm({
                  title: "E-paper already exists",
                  maskClosable: false,
                  keyboard: false,
                  content: `An e-paper for ${values?.edition} on ${moment(
                    existing.date,
                  ).format("DD-MM-YYYY")} already exists (${
                    existing.publishedAt
                      ? "Published"
                      : existing.scheduledAt
                        ? "Scheduled"
                        : "Draft"
                  }). Edit that one instead, or create a new one anyway?`,
                  okText: "Edit Existing",
                  cancelText: "Create Anyway",
                  onOk: () =>
                    navigation(`/Auth/EpaperForm/${existing.id}`, {
                      replace: true,
                    }),
                  onCancel: () => {
                    setLoading(true);
                    submit({
                      ...obj,
                      data: { ...obj.data, forceDuplicate: true },
                    });
                  },
                });
              }
            });

        submit(requestObj);
      } else {
        message.error("Sorry Pages Not Found");
      }
    } catch (err) {
      setLoading(false);
    }
  };

  const uploadPages = async (pages: any, date: any) => {
    try {
      uploadCancelledRef.current = false;
      const totalToUpload = pages.filter(
        (p: any) => p.uploaded === false,
      ).length;
      let uploadedCount = 0;
      if (totalToUpload > 0) {
        setUploadProgress({
          uploading: true,
          current: 0,
          total: totalToUpload,
        });
      }

      var arro: any = [...pages];
      for (let i = 0; i < arro?.length; i++) {
        if (uploadCancelledRef.current) break;
        if (arro[i].uploaded === false) {
          uploadedCount++;
          setUploadProgress({
            uploading: true,
            current: uploadedCount,
            total: totalToUpload,
          });
          setCurrentUploadIndex(i);
          let file = arro[i]?.file;
          const up: any = await convertApi(file, "page", i, 4, edition, date);
          if (uploadCancelledRef.current) break;
          let obj = {
            index: arro[i].index,
            imageUrl: up?.image_url,
            image: up?.image_name,
            name: arro[i]?.name,
            uploaded: true,
            server: "scaleway",
          };
          arro[i] = obj;
          setPages([...arro]);
          setCurrentUploadIndex(null);
        }
      }
      setUploadProgress({ uploading: false, current: 0, total: 0 });
      if (uploadCancelledRef.current) return null;
      return arro;
    } catch (err) {
      console.log("err = = = >", err);
      setUploadProgress({ uploading: false, current: 0, total: 0 });
      return [];
    }
  };

  const cancelSchedule = async () => {
    try {
      if (id === "create") return;
      setLoading(true);
      setType("cancel");
      const url = API.BASE_URL + API.EPAPER_EDIT + id;
      const res = await axios.put(url, { data: { scheduledAt: null } });
      setLoading(false);
      if (res?.data?.data?.id) {
        message.success("Schedule cancelled successfully");
        setData({ ...data, scheduledAt: null });
        setScheduled(null);
      }
    } catch (err) {
      setLoading(false);
      message.error("Failed to cancel schedule");
    }
  };

  const handlePublishClick = () => {
    if (data.scheduledAt && !data.publishedAt) {
      Modal.confirm({
        title: "Publish now?",
        content:
          "This e-paper is scheduled for " +
          moment(data.scheduledAt).format("DD-MM-YYYY h:mm A") +
          ". Publishing now will override the schedule.",
        okText: "Publish Now",
        cancelText: "Keep Schedule",
        onOk: () => Finished("publish"),
      });
    } else {
      Finished("publish");
    }
  };

  return (
    <div style={{ height: "95vh", overflowY: "scroll" }}>
      <PageHeader
        title={"E-Paper"}
        length={"Add New Epaper"}
        onBack={() => {
          if (id !== "create") {
            // Edit mode - go to editions table for the specific date
            const dateParam = data?.date
              ? moment(data.date).format("YYYY-MM-DD")
              : moment().format("YYYY-MM-DD");
            navigation(`/Auth/Epaper?date=${dateParam}`);
          } else {
            // Create mode - go to main E-paper page (dates table)
            navigation("/Auth/Epaper");
          }
        }}
      >
        {id !== "create" ? (
          <>
            {data.publishedAt && (
              <Tag style={{ fontSize: 14 }}>
                {moment(data.publishedAt).format("l")}
              </Tag>
            )}
            <Tag style={{ fontSize: 14 }}>{data.edition}</Tag>
            {data.publishedAt ? (
              <Tag color="green">Published</Tag>
            ) : data.scheduledAt ? (
              <Tag color="orange">
                Scheduled:{" "}
                {moment(data.scheduledAt).format("DD-MM-YYYY h:mm A")}
              </Tag>
            ) : (
              <Tag color="blue">Draft</Tag>
            )}
          </>
        ) : (
          <Tag style={{ fontSize: 14 }}>{moment().format("l")}</Tag>
        )}
      </PageHeader>
      {isloading ? (
        <Loadingbox />
      ) : (
        <Container>
          <br />
          <Row>
            <Col sm={12}>
              <Form
                form={form}
                onFinish={Finished}
                initialValues={{
                  edition: edition,
                  date: date ? dayjs(date) : null,
                }}
              >
                <Row>
                  <Col sm={4}>
                    <label className="newsFormLabels">Edition</label>
                    <Form.Item name="edition" rules={[{ required: true }]}>
                      <Select
                        size="large"
                        placeholder="Select Edition"
                        onChange={(value) => setEdition(value)}
                      >
                        {editons.map((item: any, index: any) => {
                          return (
                            <Select.Option key={index} value={item}>
                              {item}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                    <label className="newsFormLabels">Publish Date</label>
                    <Form.Item name="date" rules={[{ required: true }]}>
                      <DatePicker
                        format={"DD-MM-YYYY"}
                        defaultValue={dayjs(date)}
                        size="large"
                        style={{ width: "100%" }}
                        onChange={(value: any) => setDate(value?.$d)}
                      />
                    </Form.Item>
                    <label className="newsFormLabels">Thumbnail</label>
                    <Form.Item style={{ cursor: "pointer" }}>
                      <ThumbnailPicker
                        fileURL={thumbnail}
                        edition={edition}
                        date={date}
                        onChagnge={(value: any) => setThumbnail(value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={8}>
                    <PagesList
                      pages={pages}
                      edition={edition}
                      date={date}
                      uploadIndx={currentUploadIndex}
                      onChagnge={(value: any) => setPages(value)}
                    />
                  </Col>
                </Row>
                <hr />
                {scheduled && !data.publishedAt && (
                  <Row style={{ marginBottom: 12 }}>
                    <Col sm={4}></Col>
                    <Col sm={8}>
                      <Tag
                        color="orange"
                        style={{ fontSize: 14, padding: "4px 12px" }}
                      >
                        Scheduled: {scheduled.format("DD-MM-YYYY h:mm A")} IST
                      </Tag>
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col sm={4}></Col>
                  <Col sm={8}>
                    <Row>
                      <Col sm={3}>
                        <Button
                          block
                          style={{ height: 50 }}
                          onClick={() => Finished("save")}
                        >
                          {id !== "create" ? " Update" : "Save"}
                        </Button>
                      </Col>
                      <Col sm={3}>
                        <Button
                          disabled={!!data.publishedAt}
                          block
                          type="primary"
                          style={{ height: 50 }}
                          onClick={handlePublishClick}
                        >
                          Publish
                        </Button>
                      </Col>
                      <Col sm={3}>
                        <Button
                          disabled={!!data.publishedAt}
                          block
                          type="default"
                          style={{ height: 50, backgroundColor: "#d6d6d6ff" }}
                          onClick={() => {
                            setTempScheduled(scheduled || null);
                            setShowScheduleModal(true);
                          }}
                        >
                          {data.scheduledAt && !data.publishedAt
                            ? "Reschedule"
                            : "Schedule"}
                        </Button>
                      </Col>
                      {data.scheduledAt && !data.publishedAt && (
                        <Col sm={3}>
                          <Popconfirm
                            title="Cancel this schedule?"
                            description="The e-paper will return to Draft status."
                            onConfirm={cancelSchedule}
                            okText="Yes, Cancel"
                            cancelText="No"
                          >
                            <Button block danger style={{ height: 50 }}>
                              Cancel Schedule
                            </Button>
                          </Popconfirm>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </Form>
              <Modal
                centered
                title={
                  data.scheduledAt && !data.publishedAt
                    ? "Reschedule E-Paper"
                    : "Schedule E-Paper"
                }
                open={showScheduleModal}
                onCancel={() => setShowScheduleModal(false)}
                okText="Schedule"
                okButtonProps={{
                  disabled:
                    !tempScheduled ||
                    (tempScheduled && tempScheduled.isBefore(dayjs())),
                }}
                onOk={() => {
                  if (!tempScheduled) {
                    message.error("Please select a date and time");
                    return;
                  }
                  if (tempScheduled.isBefore(dayjs())) {
                    message.error("Cannot schedule for past date/time");
                    return;
                  }
                  setScheduled(tempScheduled);
                  setShowScheduleModal(false);
                  Finished("scheduleat", tempScheduled);
                }}
              >
                <p style={{ marginBottom: 16 }}>
                  Select the date and time to publish this e-paper:
                </p>
                <DatePicker
                  showTime={{
                    use12Hours: true,
                    format: "h:mm A",
                  }}
                  format="DD-MM-YYYY h:mm A"
                  placeholder="Select scheduled date and time"
                  size="large"
                  style={{ width: "100%" }}
                  value={tempScheduled}
                  onChange={(value: any) => {
                    if (value) {
                      const istTime = value.tz("Asia/Kolkata");
                      setTempScheduled(istTime);
                    } else {
                      setTempScheduled(null);
                    }
                  }}
                  showNow={true}
                  disabledDate={(current) => {
                    return current && current < dayjs().startOf("day");
                  }}
                />
              </Modal>
              <Modal
                open={uploadProgress.uploading}
                footer={null}
                closable={false}
                centered
                maskClosable={false}
                width={400}
              >
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 40 }} spin />
                    }
                  />
                  <h3 style={{ marginTop: 20, marginBottom: 8 }}>
                    Uploading Pages...
                  </h3>
                  <p style={{ color: "#666", marginBottom: 16 }}>
                    Uploading page {uploadProgress.current} of{" "}
                    {uploadProgress.total}
                  </p>
                  <Progress
                    percent={
                      uploadProgress.total > 0
                        ? Math.round(
                            (uploadProgress.current / uploadProgress.total) *
                              100,
                          )
                        : 0
                    }
                    status="active"
                  />
                  <p style={{ color: "#999", fontSize: 12, marginTop: 12 }}>
                    Please do not close or navigate away from this page.
                  </p>
                  <Button
                    danger
                    style={{ marginTop: 16 }}
                    onClick={() => {
                      uploadCancelledRef.current = true;
                      setUploadProgress({
                        uploading: false,
                        current: 0,
                        total: 0,
                      });
                      setLoading(false);
                      setCurrentUploadIndex(null);
                      message.info("Upload cancelled");
                    }}
                  >
                    Cancel Upload
                  </Button>
                </div>
              </Modal>
              <Modal
                open={loading && !uploadProgress.uploading}
                footer={null}
                closable={false}
                centered
                maskClosable={false}
                width={360}
              >
                <div style={{ textAlign: "center", padding: "30px 0" }}>
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 40 }} spin />
                    }
                  />
                  <h3 style={{ marginTop: 20, marginBottom: 0 }}>
                    {type === "save"
                      ? id !== "create"
                        ? "Updating..."
                        : "Saving..."
                      : type === "publish"
                        ? "Publishing..."
                        : type === "scheduleat"
                          ? "Scheduling..."
                          : type === "cancel"
                            ? "Cancelling Schedule..."
                            : "Processing..."}
                  </h3>
                </div>
              </Modal>
              <br /> <br /> <br />
            </Col>
            <Col></Col>
          </Row>
        </Container>
      )}
    </div>
  );
}

export default EpaperForm;
